import React, { useState } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Button,
  Checkbox,
  Tooltip,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import AddIcon from "@material-ui/icons/Add";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";

// feat_296 — detalle de una tarea del checklist: sus items editables inline.
// Los checks ciclan pending -> in_progress -> done -> pending.
// Los fields (texto o select) se llenan inline; se guardan al perder el foco
// (texto) o al elegir (select).

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  itemRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },
  itemLabel: { minWidth: 220, flexShrink: 0 },
  itemLabelDone: {
    minWidth: 220,
    flexShrink: 0,
    textDecoration: "line-through",
    opacity: 0.6,
  },
  grow: { flexGrow: 1 },
  addBox: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
  },
  empty: { padding: theme.spacing(2), opacity: 0.7 },
  selfDone: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  assignRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}));

// Ciclo de un check al hacer click.
const NEXT_STATUS = {
  pending: "in_progress",
  in_progress: "done",
  done: "pending",
};

const TaskDetail = ({
  task,
  onUpdateItem,
  onDeleteItem,
  onAddItem,
  onReorder,
  onToggleSelfDone,
  onAssign,
  assignees = [],
  isMaster = false,
  busy,
}) => {
  const classes = useStyles();

  const [newType, setNewType] = useState("check");
  const [newLabel, setNewLabel] = useState("");
  const [newFieldKind, setNewFieldKind] = useState("text");
  const [newOptions, setNewOptions] = useState("");
  // Buffer local de los inputs de texto: evita re-render por tecla contra la API.
  const [drafts, setDrafts] = useState({});

  const items = task.items || [];

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) return;
    const payload = { type: newType, label };
    if (newType === "field") {
      payload.fieldKind = newFieldKind;
      if (newFieldKind === "select") {
        payload.options = newOptions
          .split(",")
          .map((o) => o.trim())
          .filter(Boolean);
      }
    }
    onAddItem(payload);
    setNewLabel("");
    setNewOptions("");
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const ids = items.map((i) => i.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(target, 0, moved);
    onReorder(ids);
  };

  // Un field recién creado (o instanciado desde plantilla) llega con value=null:
  // nunca se le pasa null a un input controlado.
  const draftValue = (item) =>
    drafts[item.id] !== undefined ? drafts[item.id] : item.value ?? "";

  const commitText = (item) => {
    const next = drafts[item.id];
    if (next === undefined) return;
    setDrafts((d) => {
      const copy = { ...d };
      delete copy[item.id];
      return copy;
    });
    if ((next || "") !== (item.value || "")) {
      onUpdateItem(item.id, { value: next });
    }
  };

  const renderCheck = (item) => {
    const done = item.status === "done";
    const partial = item.status === "in_progress";
    return (
      <Tooltip
        title={
          done
            ? "Hecho — click para volver a pendiente"
            : partial
            ? "En progreso — click para marcar hecho"
            : "Pendiente — click para marcar en progreso"
        }
      >
        <Checkbox
          checked={done}
          disabled={busy}
          checkedIcon={undefined}
          indeterminate={partial}
          indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
          onChange={() =>
            onUpdateItem(item.id, { status: NEXT_STATUS[item.status] })
          }
        />
      </Tooltip>
    );
  };

  const renderField = (item) => {
    if (item.fieldKind === "select") {
      return (
        <Select
          className={classes.grow}
          value={item.value ?? ""}
          displayEmpty
          disabled={busy}
          onChange={(e) => {
            const next = e.target.value;
            onUpdateItem(item.id, { value: next });
          }}
        >
          <MenuItem value="">
            <em>Sin definir</em>
          </MenuItem>
          {(item.options || []).map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      );
    }
    return (
      <TextField
        className={classes.grow}
        size="small"
        placeholder="Sin definir"
        disabled={busy}
        value={draftValue(item)}
        onChange={(e) => {
          // ⚠ React 16 poolea el evento sintético: leer e.target DENTRO del
          // updater funcional lo encuentra ya nulificado ("Cannot read
          // properties of null (reading 'value')"). Capturar SIEMPRE antes.
          const next = e.target.value;
          setDrafts((d) => ({ ...d, [item.id]: next }));
        }}
        onBlur={() => commitText(item)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.target.blur();
        }}
      />
    );
  };

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography variant="h6">
            <span style={{ color: "#9e9e9e", marginRight: 8 }}>#{task.id}</span>
            {task.title}
          </Typography>
          {task.description && (
            <Typography variant="body2" color="textSecondary">
              {task.description}
            </Typography>
          )}
        </div>
        {busy && <CircularProgress size={22} />}
      </div>

      {/* feat_297 + feat_302 — responsable. El master reasigna a cualquiera; un
          no-master reasigna dentro de la lista que le devuelve el backend
          ([él mismo + el/los owner]) y NO ve la opción "Sin asignar": dejar sin
          responsable una tarea ajena lo sacaría de su propia vista. */}
      <div className={classes.assignRow}>
        <Typography variant="body2" color="textSecondary">
          Asignada a:
        </Typography>
        {assignees.length > 0 ? (
          <Select
            value={task.assignedTo ? String(task.assignedTo) : ""}
            displayEmpty
            disabled={busy}
            onChange={(e) => onAssign(e.target.value || null)}
            style={{ minWidth: 200 }}
          >
            {/* se renderiza siempre para que una tarea sin responsable no
                muestre el selector en blanco; el no-master no la puede elegir */}
            <MenuItem value="" disabled={!isMaster}>
              <em>Sin asignar</em>
            </MenuItem>
            {assignees.map((a) => (
              <MenuItem key={a.id} value={String(a.id)}>
                {a.name}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Typography variant="body2">
            {task.assignee ? task.assignee.name : "Sin asignar"}
          </Typography>
        )}
      </div>

      <Divider style={{ margin: "12px 0" }} />

      {items.length === 0 ? (
        <>
          <Typography variant="body2" className={classes.empty}>
            Esta tarea no tiene items. Podés marcarla como hecha directamente o
            agregarle items abajo.
          </Typography>
          <div className={classes.selfDone}>
            <Checkbox
              checked={!!task.selfDone}
              disabled={busy}
              onChange={(e) => onToggleSelfDone(e.target.checked)}
            />
            <Typography variant="body2">Tarea completada</Typography>
          </div>
        </>
      ) : (
        items.map((item, index) => {
          const complete =
            item.type === "check"
              ? item.status === "done"
              : !!(item.value && item.value.trim());
          return (
            <div key={item.id} className={classes.itemRow}>
              {item.type === "check" ? renderCheck(item) : <span style={{ width: 42 }} />}
              <Typography
                variant="caption"
                style={{ color: "#9e9e9e", marginRight: 6, flexShrink: 0 }}
              >
                #{item.id}
              </Typography>
              <Typography
                variant="body2"
                className={complete ? classes.itemLabelDone : classes.itemLabel}
              >
                {item.label}
              </Typography>
              {item.type === "field" ? renderField(item) : <span className={classes.grow} />}
              <IconButton
                size="small"
                disabled={busy || index === 0}
                onClick={() => move(index, -1)}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={busy || index === items.length - 1}
                onClick={() => move(index, 1)}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={busy}
                onClick={() => onDeleteItem(item.id)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </div>
          );
        })
      )}

      <Divider style={{ margin: "12px 0" }} />

      <div className={classes.addBox}>
        <Select
          value={newType}
          onChange={(e) => setNewType(e.target.value)}
          style={{ minWidth: 110 }}
        >
          <MenuItem value="check">Check</MenuItem>
          <MenuItem value="field">Campo</MenuItem>
        </Select>
        {newType === "field" && (
          <Select
            value={newFieldKind}
            onChange={(e) => setNewFieldKind(e.target.value)}
            style={{ minWidth: 110 }}
          >
            <MenuItem value="text">Texto</MenuItem>
            <MenuItem value="select">Lista</MenuItem>
          </Select>
        )}
        <TextField
          size="small"
          label="Etiqueta"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          style={{ minWidth: 220 }}
        />
        {newType === "field" && newFieldKind === "select" && (
          <TextField
            size="small"
            label="Opciones (separadas por coma)"
            value={newOptions}
            onChange={(e) => setNewOptions(e.target.value)}
            style={{ minWidth: 260 }}
          />
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={busy || !newLabel.trim()}
          onClick={handleAdd}
        >
          Agregar item
        </Button>
      </div>
    </Paper>
  );
};

export default TaskDetail;
